import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { TileHeader, LanguageCountrySelection } from '@bingads-webui-campaign-react/goals-and-website-tile';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ServerContextProvider } from '@bingads-webui-campaign-react/shared-app-capi';
import { ServerContext, useUpdateServerContext } from '@bingads-webui-campaign-react/server-context';
import { Subtitle2 } from '@fluentui/react-components';
import {
  globalizationModelSetup,
  LANGUAGE_STRINGS,
} from '@bingads-webui-campaign/ads-globalization';
import {
  TermsAndConditions as TermsAndConditionsComponent,
  withClientCenterApiContext,
} from '@bingads-webui-campaign-react/business-and-payment-setup';
import { useFormService } from '@bingads-webui-fluent-component/form';

import { isSignupRunningProxy } from './business-info-helper';
import { HorizontalDivider } from './horizontal-divider';
import { BusinessInfoCollector } from './business-info-collector';
import { getLanguageCountryStepStyles } from './language-country-step-styles';

const TermsAndConditions = withClientCenterApiContext(props => (<TermsAndConditionsComponent {...props} />));

export const LanguageCountryStep = ({
  data,
  onDataChange,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getLanguageCountryStepStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const permissions = useContext(PermissionsContext);
  const serverContext = useContext(ServerContext);
  const updateServerContextFn = useUpdateServerContext();
  const { state: formState } = useFormService();
  const { saveFailed } = formState.context;
  const { CurrentLocale } = serverContext;
  const isDebugging = isSignupRunningProxy();

  const isCollectBusinessInfoEnabled = usePilotsValue('IsPmaxLiteCollectBusinessInfoEnabled');

  const {
    supportedCountryList,
    selectedLanguage,
    selectedCountry,
    userContactInfo = {},
    hideTerms,
  } = data || {};

  const { LANGUAGES } = globalizationModelSetup({ permissions, culture: CurrentLocale });

  const languages = useMemo(() => {
    const options = _.map(LANGUAGES, lang => ({
      key: lang,
      text: i18n.getString(LANGUAGE_STRINGS[lang]),
    }));
    return _.sortBy(options, 'text');
  }, [i18n, LANGUAGES]);

  const onLanguageSelected = useCallback((_event, selection) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        selectedLanguage: selection.key,
      }),
    });
  }, [onDataChange]);

  const countryOptions = useMemo(() => {
    const options = _.map(supportedCountryList, country => ({
      key: country.Code,
      text: country.Name,
    }));
    return _.sortBy(options, 'text');
  }, [supportedCountryList]);

  const onCountrySelected = useCallback((_event, selection) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        selectedCountry: selection.key,
      }),
    });
  }, [onDataChange]);

  const onEmailChange = useCallback((newEmail) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        userContactInfo: {
          ...userContactInfo,
          email: newEmail,
        },
      }),
    });
  }, [onDataChange, userContactInfo]);

  const onPhoneChange = useCallback(({
    changePhoneNumber, phoneNumber, callingCode, callingCountryCode,
  }) => {
    if (changePhoneNumber) {
      onDataChange({
        data: prevData => ({
          ...prevData,
          userContactInfo: {
            ...userContactInfo,
            phoneNumber,
          },
        }),
      });
    } else {
      onDataChange({
        data: prevData => ({
          ...prevData,
          userContactInfo: {
            ...userContactInfo,
            callingCode,
            callingCountryCode,
          },
        }),
      });
    }
  }, [onDataChange, userContactInfo]);

  const onTermAndConditionChange = useCallback(({ accepted, communication }) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        terms: {
          accepted,
          communication: {
            // make sure non-nullable properties are always sent to the server
            AllowContact: false,
            ...communication,
          },
        },
      }),
    });
  }, [onDataChange]);

  const onBusinessInfoValidated = useCallback((isValid) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        userContactInfo: {
          ...userContactInfo,
          isValid,
        },
      }),
    });
  }, [onDataChange, userContactInfo]);

  if (!data) {
    return null;
  }

  return (
    <React.Fragment>
      <div
        className={classNames(
          'language-country-step-container',
          classes.container,
          isCollectBusinessInfoEnabled && classes.containerWithBusinessInfo
        )}
      >
        <ServerContextProvider serverContext={serverContext} updateServerContext={updateServerContextFn} >
          <TileHeader
            title={i18n.getString(_TL_('Tell us about your business'))}
            displayMode={displayMode}
          />
          {
            isCollectBusinessInfoEnabled &&
            <Subtitle2 className="section-subtitle">{i18n.getString(_TL_('Business Infomation'))}</Subtitle2>
          }
          <LanguageCountrySelection
            selectedLanguage={selectedLanguage}
            onLanguageSelected={onLanguageSelected}
            languages={languages}
            countryOptions={countryOptions}
            selectedCountry={selectedCountry}
            onCountrySelected={onCountrySelected}
            noShadow
          />
          {
            isCollectBusinessInfoEnabled &&
            <div className="collect-business-info-container">
              <HorizontalDivider />
              <Subtitle2 className="section-subtitle">{i18n.getString(_TL_('Primary contact'))}</Subtitle2>
              <BusinessInfoCollector
                alwaysShowError={saveFailed}
                email={userContactInfo.email}
                onEmailChange={onEmailChange}
                selectedCountryCode={userContactInfo.callingCountryCode}
                countries={supportedCountryList}
                phoneNumber={userContactInfo.phoneNumber}
                onPhoneChange={onPhoneChange}
                setIsValid={onBusinessInfoValidated}
              />
              {
                !hideTerms && !isDebugging &&
                <div>
                  <HorizontalDivider />
                  <Subtitle2 className="section-subtitle">{i18n.getString(_TL_('Agreement'))}</Subtitle2>
                  <div className="terms-and-conditions-container">
                    <TermsAndConditions
                      country={selectedCountry}
                      onChange={onTermAndConditionChange}
                      showValidationResult={saveFailed}
                    />
                  </div>
                </div>
              }
            </div>
          }
        </ServerContextProvider>
      </div>
    </React.Fragment >
  );
};

LanguageCountryStep.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
